import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import {
  createCustomChecklistTask,
  CreateCustomChecklistTaskRequest,
  CustomChecklistTaskResponse,
} from "@/api/createCustomChecklistTask";
import {
  fetchCustomChecklistTasks,
  fetchCustomChecklistTask,
} from "@/api/fetchCustomChecklistTasks";
import {
  updateCustomChecklistTask,
  UpdateCustomChecklistTaskRequest,
} from "@/api/updateCustomChecklistTask";
import { deleteCustomChecklistTask } from "@/api/deleteCustomChecklistTask";

/**
 * Custom Checklist Tasks Slice
 * Manages CRUD operations for custom checklist task templates
 * Part of Feature 020: Custom Checklist Tasks
 */

// State interface
interface CustomChecklistTasksState {
  tasks: CustomChecklistTaskResponse[];
  selectedTask: CustomChecklistTaskResponse | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  createError: string | null;
  updateLoading: boolean;
  updateError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  total: number;
}

// Initial state
const initialState: CustomChecklistTasksState = {
  tasks: [],
  selectedTask: null,
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  deleteLoading: false,
  deleteError: null,
  total: 0,
};

// Async Thunks

/**
 * Fetch all custom checklist tasks for the company
 */
export const fetchTasks = createAsyncThunk(
  "customChecklistTasks/fetchTasks",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchCustomChecklistTasks();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch custom checklist tasks",
      );
    }
  },
);

/**
 * Fetch a single custom checklist task by ID
 */
export const fetchTask = createAsyncThunk(
  "customChecklistTasks/fetchTask",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetchCustomChecklistTask(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch custom checklist task",
      );
    }
  },
);

/**
 * Create a new custom checklist task
 */
export const createTask = createAsyncThunk(
  "customChecklistTasks/createTask",
  async (payload: CreateCustomChecklistTaskRequest, { rejectWithValue }) => {
    try {
      const response = await createCustomChecklistTask(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to create custom checklist task",
      );
    }
  },
);

/**
 * Update an existing custom checklist task
 */
export const updateTask = createAsyncThunk(
  "customChecklistTasks/updateTask",
  async (
    { id, payload }: { id: string; payload: UpdateCustomChecklistTaskRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateCustomChecklistTask(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update custom checklist task",
      );
    }
  },
);

/**
 * Delete a custom checklist task
 */
export const deleteTask = createAsyncThunk(
  "customChecklistTasks/deleteTask",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteCustomChecklistTask(id);
      return id;
    } catch (error: any) {
      // Handle 409 Conflict (task in use) specially
      if (error.response?.status === 409) {
        return rejectWithValue(
          error.response?.data?.error ||
            "Cannot delete task: it is in use in learning paths",
        );
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete custom checklist task",
      );
    }
  },
);

// Slice
const customChecklistTasksSlice = createSlice({
  name: "customChecklistTasks",
  initialState,
  reducers: {
    setSelectedTask: (
      state,
      action: PayloadAction<CustomChecklistTaskResponse | null>,
    ) => {
      state.selectedTask = action.payload;
    },
    clearSelectedTask: (state) => {
      state.selectedTask = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    clearTasks: (state) => {
      state.tasks = [];
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks;
        state.total = action.payload.total;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch single task
    builder
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTask = action.payload;
        // Also update in tasks array if it exists
        const index = state.tasks.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create task
    builder
      .addCase(createTask.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.createLoading = false;
        state.tasks.push(action.payload);
        state.total += 1;
        state.selectedTask = action.payload;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
      });

    // Update task
    builder
      .addCase(updateTask.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.tasks.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        // Update selected task if it's the same
        if (state.selectedTask?.id === action.payload.id) {
          state.selectedTask = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });

    // Delete task
    builder
      .addCase(deleteTask.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
        state.total -= 1;
        // Clear selection if deleted
        if (state.selectedTask?.id === action.payload) {
          state.selectedTask = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });
  },
});

// Actions
export const { setSelectedTask, clearSelectedTask, clearErrors, clearTasks } =
  customChecklistTasksSlice.actions;

// Selectors
export const selectAllTasks = (state: RootState) =>
  state.customChecklistTasks.tasks;

export const selectTaskById = (id: string) => (state: RootState) => {
  return state.customChecklistTasks.tasks.find((task) => task.id === id);
};

export const selectSelectedTask = (state: RootState) =>
  state.customChecklistTasks.selectedTask;

export const selectTasksLoading = (state: RootState) =>
  state.customChecklistTasks.loading;

export const selectTasksError = (state: RootState) =>
  state.customChecklistTasks.error;

export const selectCreateLoading = (state: RootState) =>
  state.customChecklistTasks.createLoading;

export const selectCreateError = (state: RootState) =>
  state.customChecklistTasks.createError;

export const selectUpdateLoading = (state: RootState) =>
  state.customChecklistTasks.updateLoading;

export const selectUpdateError = (state: RootState) =>
  state.customChecklistTasks.updateError;

export const selectDeleteLoading = (state: RootState) =>
  state.customChecklistTasks.deleteLoading;

export const selectDeleteError = (state: RootState) =>
  state.customChecklistTasks.deleteError;

export const selectTasksTotal = (state: RootState) =>
  state.customChecklistTasks.total;

// Reducer
export default customChecklistTasksSlice.reducer;
